import { Fragment } from 'react'

import s from './HorizonFooter.module.css'


/**
 * When a source last confirmed the listings — `max(last_seen_at)`, not a clock and not the
 * build time. It is the one fact a reader needs about a calendar somebody runs off a
 * laptop: not who keeps it or when the page was rendered, but whether anything has looked
 * at London lately.
 */
const STAMP = new Intl.DateTimeFormat('en-GB', {
  // The listings are London's, so the stamp is London's. A reader in another zone wants to
  // know when a London source last ran, not what their own clock said at that instant.
  timeZone: 'Europe/London',
  weekday: 'short',
  day: 'numeric',
  // `month: 'numeric'`, not 'short'. CLDR's en-GB abbreviation for September is "Sept", so
  // `month: 'short'` prints "SEPT" where the Python's `_MONS` prints "SEP" — the one month
  // of twelve where the two disagree, which is exactly the kind of difference that ships.
  // The table below is the Python's, so all twelve agree by construction.
  month: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  // `hour12: false` yields "24:05" for five past midnight in some engines; h23 does not.
  hourCycle: 'h23',
})

/** `_landing_page._MONS`, verbatim. */
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

function part(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find(p => p.type === type)?.value ?? ''
}

function updatedText(stamp: string | null): string {
  if (!stamp) return ''
  const when = new Date(stamp)
  // The Python would raise on an unparseable stamp; a footer is the wrong place to take the
  // page down over one, so an unreadable stamp reads as no stamp.
  if (Number.isNaN(when.getTime())) return ''
  const parts = STAMP.formatToParts(when)
  // Uppercased rather than cased by CSS as well, because the Python's `_DAYS`/`_MONS` are
  // uppercase constants and this string is also handed to callers that are not this sheet.
  const day = part(parts, 'weekday').toUpperCase()
  const month = MONTHS[Number(part(parts, 'month')) - 1] ?? ''
  return (
    `Last updated ${day} ${part(parts, 'day')} ${month} ${part(parts, 'year')}` +
    ` · ${part(parts, 'hour')}:${part(parts, 'minute')}`
  )
}

export interface HorizonFooterProps {
  /** ISO instant, or null when nothing has been seen yet. */
  updatedAt: string | null
  /** Instagram handles, without the `@`. Empty means the site has no accounts configured. */
  handles: string[]
  /**
   * Whether anybody is signed in. `undefined` while that is still being found out.
   *
   * The footer took no viewer at all and hard-coded the link, so it said SIGN IN to people
   * who were signed in, on every page, six inches under a masthead correctly showing their
   * name.
   */
  signedIn?: boolean | undefined
}

export function HorizonFooter({ updatedAt, handles, signedIn }: HorizonFooterProps) {
  return (
    // A `<footer>`, not a `<div>`. It was the latter, inside `<main>`, so the document
    // offered a header and a nav as its regions and no footer at all.
    <footer className={s.horizon}>
      {/* Decorative: the picture says nothing the type below it does not.

          Drawn only once the viewer is known — the same `undefined` the sign-in link waits
          on, which is `ViewerProvider` saying the shell has not heard from the server yet.
          The shell paints before any route has data, so an `<img>` here from the first
          render sits inside the empty viewport, `lazy` does not defer it, and the picture
          starts down the connection the page's own data is on. The band wrapping it keeps
          the picture's aspect ratio whether or not the `<img>` is in it, so adding the
          picture moves nothing below. */}
      <div className={s.art}>
        {signedIn === undefined ? null : (
          <img src="/footer" alt="" loading="lazy" decoding="async" fetchPriority="low" />
        )}
      </div>

      <p className={s.colophon}>
        {/* Empty when there is no stamp, and still emitted — it is one of the three flex
            items `space-between` distributes, and dropping it moves the other two. */}
        <span>{updatedText(updatedAt)}</span>

        {handles.length > 0 && (
          <span>
            {handles.map((handle, i) => (
              <Fragment key={handle}>
                {i > 0 && ' · '}
                <a href={`https://instagram.com/${handle}`} rel="me noreferrer" target="_blank">
                  @{handle}
                </a>
              </Fragment>
            ))}
          </span>
        )}

        {/* Literal middot characters throughout, never `&middot;`. The Python page could use
            the entity because it dropped these into markup, but the calendar built the same
            footer in the browser and escaped what it was given, so the entity arrived on
            screen as the five characters `&middot;`. React escapes text the same way. */}
        {/* The offer only where there is somebody to take it. Nothing replaces it when
            there is not: the masthead already carries the account, and a second link to it
            down here would be the same fact twice rather than a fact this slot needs. */}
        {/* The calendar link is gone: the masthead carries it on every page, and a footer
            repeating the navigation above it is the same fact twice. The sign-in stays,
            because that is an offer rather than a route. */}
        {signedIn === false ? (
          <span>
            <a href="/login">sign in</a>
          </span>
        ) : null}
      </p>
    </footer>
  )
}
